import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { Language, LANGUAGES } from '../types/language';

interface LanguageSelectorProps {
  currentLanguage: Language;
  onLanguageChange: (language: Language) => void;
}

const LanguageSelector: React.FC<LanguageSelectorProps> = ({
  currentLanguage,
  onLanguageChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const currentLangConfig = LANGUAGES.find(lang => lang.code === currentLanguage) || LANGUAGES[0];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLanguageSelect = (language: Language) => {
    onLanguageChange(language);
    setIsOpen(false);
  };

  return (
    <LanguageSelectorContainer ref={dropdownRef}>
      <LanguageButton
        onClick={() => setIsOpen(!isOpen)}
        $isOpen={isOpen}
      >
        <LanguageFlag>{currentLangConfig.flag}</LanguageFlag>
        <LanguageName>{currentLangConfig.name}</LanguageName>
        <ChevronIcon $isOpen={isOpen}>
          <svg width="12" height="8" viewBox="0 0 12 8" fill="none">
            <path d="M1 1L6 6L11 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </ChevronIcon>
      </LanguageButton>

      {isOpen && (
        <LanguageDropdown>
          {LANGUAGES.map((lang) => (
            <LanguageOption
              key={lang.code}
              onClick={() => handleLanguageSelect(lang.code)}
              $isSelected={lang.code === currentLanguage}
            >
              <LanguageFlag>{lang.flag}</LanguageFlag>
              <LanguageName>{lang.name}</LanguageName>
              {lang.code === currentLanguage && (
                <CheckIcon>
                  <svg width="14" height="10" viewBox="0 0 14 10" fill="none">
                    <path d="M1 5L5 9L13 1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </CheckIcon>
              )}
            </LanguageOption>
          ))}
        </LanguageDropdown>
      )}
    </LanguageSelectorContainer>
  );
};

export default LanguageSelector;

const LanguageSelectorContainer = styled.div`
  position: relative;
  z-index: 1000;
`;

const LanguageButton = styled.button<{ $isOpen: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: white;
  border: 1.5px solid ${props => props.$isOpen ? '#74b9ff' : '#e1e8f0'};
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 14px;
  font-weight: 500;
  color: #33363F;
  min-width: 120px;
  justify-content: space-between;
  box-shadow: ${props => props.$isOpen ? '0 0 0 3px rgba(116, 185, 255, 0.1)' : '0 2px 4px rgba(0, 0, 0, 0.05)'};

  &:hover {
    border-color: #74b9ff;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }

  &:focus {
    outline: none;
    border-color: #74b9ff;
    box-shadow: 0 0 0 3px rgba(116, 185, 255, 0.1);
  }
`;

const LanguageFlag = styled.span`
  font-size: 16px;
  line-height: 1;
`;

const LanguageName = styled.span`
  flex: 1;
  text-align: left;
  white-space: nowrap;
`;

const ChevronIcon = styled.div<{ $isOpen: boolean }>`
  color: #666;
  transition: transform 0.2s ease;
  transform: ${props => props.$isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
  display: flex;
  align-items: center;
  
  svg {
    width: 12px;
    height: 8px;
  }
`;

const LanguageDropdown = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  background: white;
  border: 1.5px solid #e1e8f0;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  overflow: hidden;
  min-width: 140px;
  animation: slideDown 0.2s ease-out;

  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-8px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const LanguageOption = styled.button<{ $isSelected: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: ${props => props.$isSelected ? '#f8f9ff' : 'white'};
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 14px;
  font-weight: ${props => props.$isSelected ? '600' : '500'};
  color: ${props => props.$isSelected ? '#22468A' : '#33363F'};
  text-align: left;
  justify-content: space-between;

  &:hover {
    background: ${props => props.$isSelected ? '#f8f9ff' : '#f5f6fa'};
  }

  &:focus {
    outline: none;
    background: #f8f9ff;
  }

  &:not(:last-child) {
    border-bottom: 1px solid #f0f0f0;
  }
`;

const CheckIcon = styled.div`
  color: #22468A;
  display: flex;
  align-items: center;
  
  svg {
    width: 14px;
    height: 10px;
  }
`;
